import React from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Button,
  Paper
} from '@mui/material';
import { SimulationSelectionProps } from './types';

const SimulationSelection: React.FC<SimulationSelectionProps> = ({
  availableSimulations,
  selectedSimulations,
  onSelectionChange,
  onLoadReport,
  canLoadReport
}) => {
  const handleSimulationToggle = (simulationId: string) => {
    const newSelection = selectedSimulations.includes(simulationId)
      ? selectedSimulations.filter(id => id !== simulationId)
      : [...selectedSimulations, simulationId];
    onSelectionChange(newSelection);
  };

  const handleSelectAll = () => {
    const allIds = availableSimulations.map(sim => sim.simulation_id).filter(id => id);
    onSelectionChange(allIds);
  };

  const handleSelectNone = () => {
    onSelectionChange([]);
  };

  return (
    <Paper sx={{ p: 2, mb: 2, bgcolor: '#2a3441', color: 'white' }}>
      <FormControl component="fieldset" sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <FormLabel component="legend" sx={{ 
            color: 'white !important', 
            fontSize: '1.1rem', 
            fontWeight: 'bold',
            '&.Mui-focused': { color: 'white !important' },
            '&.MuiFormLabel-root': { color: 'white !important' }
          }}>
            Select Simulations to Include
          </FormLabel>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSelectAll}
              sx={{ color: '#00d4ad', borderColor: '#00d4ad' }}
            >
              Select All
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSelectNone}
              sx={{ color: '#ff5252', borderColor: '#ff5252' }}
            >
              Select None
            </Button>
          </Box>
        </Box>
        
        <FormGroup>
          {availableSimulations.map((simulation) => {
            const simulationId = simulation.simulation_id;
            const simulationName = simulation.name || `Simulation ${simulationId}`;
            
            // Skip simulations without simulation_id
            if (!simulationId) return null;
            
            return (
              <FormControlLabel
                key={simulationId}
                control={
                  <Checkbox
                    checked={selectedSimulations.includes(simulationId)}
                    onChange={() => handleSimulationToggle(simulationId)}
                    sx={{
                      color: '#00d4ad',
                      '&.Mui-checked': {
                        color: '#00d4ad',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {simulationName}
                  </Typography>
                }
                sx={{ mb: 1 }}
              />
            );
          })}
        </FormGroup>
        
        {availableSimulations.length === 0 && (
          <Typography variant="body2" sx={{ color: '#888', textAlign: 'center', py: 2 }}>
            No simulations available
          </Typography>
        )}
        
        {/* Load Report Button */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={onLoadReport}
            disabled={!canLoadReport || selectedSimulations.length === 0}
            sx={{
              bgcolor: '#00d4ad',
              color: 'white',
              fontWeight: 'bold',
              px: 4,
              '&:hover': {
                bgcolor: '#00b895',
              },
              '&:disabled': {
                bgcolor: '#555',
                color: '#888',
              },
            }}
          >
            Load Report ({selectedSimulations.length} simulation{selectedSimulations.length !== 1 ? 's' : ''})
          </Button>
        </Box>
      </FormControl>
    </Paper>
  );
};

export default SimulationSelection;
